import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch } from "../redux/store";
import tinycolor from "tinycolor2";

import { updateAppointment } from "../redux/slices/appointmentSlice";
import { BookingStage } from "../constants/bookingStages";
import { validateNumber } from "../utils";
import { compileTemplate } from "../utils/formatting";
import { getGradientFromColor } from "../utils/theme";
import { ensureBookingId } from "../utils/bailout";

const CustomerInput = ({ appointment }) => {
  const [valueCheck, setValueCheck] = useState(["cheater"]);
  const [inputClicked, setInputClicked] = useState({});
  const [errors, setErrors] = useState({});
  const [customer, setCustomer] = useState({});
  const [loading] = useState(false);
  const [autoCheckingCustomer] = useState(false);
  const [creatingBookingId, setCreatingBookingId] = useState(false);
  const initialPhoneValidRef = useRef(
    Boolean(
      appointment?.customer?.phone && validateNumber(appointment.customer.phone)
    )
  );

  const dispatch = useDispatch();

  // Accent color & gradient (matching new UI pattern)
  const accentColor = appointment?.shopData?.color || "#2b73b9";
  const { gradientBackground } = useMemo(
    () => getGradientFromColor(accentColor),
    [accentColor]
  );
  const accentLight = useMemo(
    () => tinycolor(accentColor).lighten(25).toHexString(),
    [accentColor]
  );

  const smsShopName = appointment?.shopData?.shopName?.name || "my service center";
  const smsOptInTitle = appointment?.shopData?.messages?.smsOptInTitle || "Receive Appointment Confirmation Reminders";
  const smsOptInMessage = useMemo(() => {
    const raw =
      appointment?.shopData?.messages?.smsOptInMessage ||
      "By checking this box, I agree to receive informational SMS messages from {{shop_name}} regarding appointment reminders and service updates. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is not required to book service.";
    return compileTemplate(raw, { shop_name: smsShopName });
  }, [appointment?.shopData?.messages?.smsOptInMessage, smsShopName]);

  const formFields = [
    {
      type: "text",
      name: "phone",
      required: true,
      placeholder: "Phone Number",
    },
  ];

  useEffect(() => {
    if (appointment?.customer?.phone && !customer.phone) {
      setCustomer({ phone: appointment.customer.phone });
      setValueCheck([]);
    }
  }, [appointment?.customer?.phone]);

  // If a valid phone number already exists from before this screen,
  // auto-advance once. Do not trigger just because the user typed here.
  useEffect(() => {
    const phone = appointment?.customer?.phone;
    if (
      appointment?.stage === BookingStage.CustomerInfo &&
      appointment?.serviceOptionsPhoneCaptured &&
      initialPhoneValidRef.current &&
      phone &&
      validateNumber(phone) &&
      !appointment?.customerInputAutoAdvanced
    ) {
      dispatch(
        updateAppointment({
          updates: {
            stage: appointment?.skipToForm ? 7 : (appointment?.stage || 0) + 1,
            customerInputAutoAdvanced: true,
          },
        })
      );
    }
  }, [
    appointment?.customer?.phone,
    appointment?.skipToForm,
    appointment?.stage,
    appointment?.serviceOptionsPhoneCaptured,
    appointment?.customerInputAutoAdvanced,
  ]);

  useEffect(() => {
    let updatedValues = [];
    formFields.forEach((field) => {
      if (field.required && !customer[field.name]) {
        updatedValues.push(field.name);
      }
    });

    // If phone exists but is invalid, keep it required
    if (customer.phone !== undefined) {
      const phoneNumber = validateNumber(customer.phone);
      if (!phoneNumber) {
        if (!updatedValues.includes("phone")) {
          updatedValues.push("phone");
        }
      }
    }

    setValueCheck(updatedValues);
  }, [customer]);

  // Automatically create booking ID when valid phone number is entered on this page
  useEffect(() => {
    let isMounted = true;
    const phone = appointment?.customer?.phone;
    
    if (
      phone &&
      validateNumber(phone) &&
      !appointment?.bookingId &&
      !appointment?.serviceOptionsPhoneCaptured && // Only if they entered it here, not earlier
      !creatingBookingId // Prevent duplicate calls while one is in-flight
    ) {
      const payload = {
        ...appointment,
        customer: { ...appointment?.customer, phone },
      };
      
      setCreatingBookingId(true);
      
      ensureBookingId({
        appointment,
        payload,
        dispatch,
        updateAppointmentAction: updateAppointment,
      })
        .then(() => {
          if (isMounted) {
            setCreatingBookingId(false);
          }
        })
        .catch((error) => {
          console.error("Error creating booking ID from CustomerInput phone capture:", error);
          if (isMounted) {
            setCreatingBookingId(false);
          }
        });
    }

    return () => {
      isMounted = false;
    };
  }, [appointment?.customer?.phone, appointment?.bookingId, appointment?.serviceOptionsPhoneCaptured, creatingBookingId]);

  const updateCustomerInfo = (e) => {
    let fieldName = e.target.name;
    let fieldValue = e.target.value;

    if (fieldName === "phone") {
      // Allow free typing; keep Next disabled until valid
      if (errors.phone) {
        const newErrors = { ...errors };
        delete newErrors.phone;
        setErrors(newErrors);
      }
      setCustomer({ ...customer, [fieldName]: fieldValue });
      // Ensure phone remains required while typing unless valid
      const phoneValid = Boolean(validateNumber(fieldValue));
      if (!phoneValid) {
        if (!valueCheck.includes("phone")) {
          setValueCheck((prev) => [...prev, "phone"]);
        }
      }
      dispatch(
        updateAppointment({
          updates: {
            customer: {
              ...appointment?.customer,
              [fieldName]: fieldValue,
            },
            serviceOptionsPhoneCaptured: false,
          },
        })
      );
    } else {
      setCustomer({ ...customer, [fieldName]: fieldValue });
      dispatch(
        updateAppointment({
          updates: {
            customer: {
              ...appointment?.customer,
              [fieldName]: fieldValue,
            },
          },
        })
      );
    }
  };

  const handleFocus = (fieldName) => {
    setInputClicked({ ...inputClicked, [fieldName]: true });
    if (fieldName === "phone" && errors.phone) {
      // Clear error on focus so user can type
      const newErrors = { ...errors };
      delete newErrors.phone;
      setErrors(newErrors);
    }
  };

  const handleBlur = (e, isRequired) => {
    let updatedValues;

    if (e.target.name === "phone") {
      const rawPhone = e.target.value;
      const phoneNumber = validateNumber(rawPhone);
      const phoneDigits = rawPhone.replace(/\D/g, "");
      if (/^\s*$/.test(rawPhone)) {
        setErrors({
          ...errors,
          phone: "Phone number cannot be empty or spaces",
        });
        if (!valueCheck.includes("phone"))
          setValueCheck((prev) => [...prev, "phone"]);
        return;
      } else if (phoneDigits.length < 10) {
        // Wait to show invalid formatting feedback until user has enough digits.
        const newErrors = { ...errors };
        delete newErrors.phone;
        setErrors(newErrors);
        if (!valueCheck.includes("phone"))
          setValueCheck((prev) => [...prev, "phone"]);
        return;
      } else if (!phoneNumber) {
        setErrors({ ...errors, phone: "Invalid phone number" });
        if (!valueCheck.includes("phone"))
          setValueCheck((prev) => [...prev, "phone"]);
        return;
      } else {
        setCustomer({ ...customer, phone: rawPhone });
        const newErrors = { ...errors };
        delete newErrors.phone;
        setErrors(newErrors);
        updatedValues = valueCheck?.filter((item) => item !== "phone");
        setValueCheck(updatedValues);
      }
    } else {
      if (e.target.value !== "") {
        updatedValues = valueCheck?.filter((item) => item !== e.target.name);
        setValueCheck(updatedValues);
        setCustomer({ ...customer, [e.target.name]: e.target.value });
        updateCustomerInfo(e);
      } else if (!valueCheck?.includes(e.target.name) && isRequired) {
        setValueCheck((oldValues) => [...oldValues, e.target.name]);
      }
    }
  };

  if (loading && autoCheckingCustomer) {
    return (
      <div
        className="px-4 py-5 md:px-6 md:py-6 min-h-full font-roboto"
        style={{ background: gradientBackground }}
      >
        <div className="flex justify-center items-center py-12">
          <div className="flex flex-col items-center justify-center text-center">
            <i
              className="fa-solid fa-tire-rugged animate-spin text-6xl"
              aria-hidden="true"
              style={{ color: "#6b6b6b", lineHeight: 1 }}
            />
            <p className="text-sm text-gray-600 mt-3">
              Checking your account...
            </p>
          </div>
        </div>
      </div>
    );
  }

  const hasErrors = Object.keys(errors).length > 0;
  const phoneValue = customer.phone || "";

  return (
    <div
      className="px-4 py-5 md:px-6 md:py-6 min-h-full font-roboto"
      style={{ background: gradientBackground }}
    >
      <div
        className="rounded-2xl bg-white p-5"
        style={{
          border: `2px solid ${accentColor}33`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <h2
          className="text-lg font-bold mb-4 text-center"
          style={{ color: "#1f2937" }}
        >
          {appointment?.customer?.phone && appointment?.customerLookupProcessed
            ? "Update your phone number if needed"
            : "Enter your phone number to look up your account or create one"}
        </h2>

        {hasErrors && (
          <div
            className="mb-4 p-3 rounded-xl text-sm"
            style={{
              backgroundColor: tinycolor("#ef4444").setAlpha(0.1).toRgbString(),
              color: "#ef4444",
            }}
          >
            <ul className="list-disc list-inside">
              {Object.keys(errors).map((error, index) => (
                <li key={index}>{errors[error]}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Phone Number Input */}
        <div className="flex flex-col mb-4">
          <label
            htmlFor="phone"
            className="text-sm font-semibold mb-1"
            style={{ color: "#1f2937" }}
          >
            Phone Number
          </label>
          <input
            type="text"
            name="phone"
            id="phone"
            placeholder="Phone Number"
            required
            value={phoneValue}
            maxLength={20}
            className="w-full box-border p-2 border-2 rounded-lg text-sm"
            style={{
              border: hasErrors && errors.phone
                ? "2px solid #ef4444"
                : `2px solid ${accentColor}66`,
              color: "#111827",
              backgroundColor: "#ffffff",
              outlineColor: accentColor,
            }}
            onFocus={() => handleFocus("phone")}
            // One handler, in the order React ran onInput then onChange:
            // preact/compat would register onChange as a capture listener
            // and run it first.
            onChange={(e) => {
              updateCustomerInfo(e);
              handleBlur(e, true);
            }}
            data-testid="customer-phone"
          />
        </div>

        {/* SMS Opt-In Card (styled like service selection cards) */}
        {(() => {
          const isSmsOptedIn = appointment?.customer?.optOut === false;
          return (
            <div
              className="flex items-center gap-3 rounded-[16px] p-3 cursor-pointer transition-shadow font-roboto"
              onClick={() => {
                dispatch(
                  updateAppointment({
                    updates: {
                      customer: {
                        ...appointment?.customer,
                        optOut: appointment?.customer?.optOut === false ? true : false,
                      },
                    },
                  })
                );
              }}
              style={{
                backgroundColor: isSmsOptedIn ? accentColor : "#ffffff",
                border: `2px solid ${accentLight}`,
                boxShadow: isSmsOptedIn
                  ? `0 6px 16px ${accentColor}55`
                  : "0 1px 4px rgba(0,0,0,0.08)",
              }}
            >
              {/* Checkbox */}
              <div className="flex-shrink-0 self-center">
                <div
                  className="flex items-center justify-center w-7 h-7 rounded-md"
                  style={{
                    border: `2px solid ${accentColor}`,
                    backgroundColor: isSmsOptedIn ? "rgba(255,255,255,0.25)" : "#f3f4f6",
                  }}
                >
                  {isSmsOptedIn && (
                    <i
                      className="fa-solid fa-check"
                      aria-hidden="true"
                      style={{ color: "#ffffff", fontSize: "14px", lineHeight: 1 }}
                    />
                  )}
                </div>
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <div
                  className="text-sm font-bold leading-tight"
                  style={{ color: isSmsOptedIn ? "#ffffff" : accentColor }}
                >
                  {smsOptInTitle}
                </div>
                <div
                  className="mt-0.5 text-xs leading-snug"
                  style={{
                    color: isSmsOptedIn ? "rgba(255,255,255,0.85)" : "#6b7280",
                  }}
                >
                  {smsOptInMessage}
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export { CustomerInput };
